import {
  Alert,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { QuotesTeammates } from "components/Carrier/components/QuotesTeammates";
import { CurrencySelect } from "components/CurrencySelect";
import { EquipmentTypeSelect } from "components/EquipmentTypeSelect";
import { NewTabLink } from "components/NewTabLink";
import { NumberInput } from "components/NumberInput";
import { formatQuotingUnitOfMeasure } from "utils/quotes";

import { QUOTES_ONLY_SHIPMENT_BLOG_URL } from "../../../../../../constants";

import type { SubmitQuoteFormShipment } from "components/QuoteFields/types";

import type { SubmitQuoteFormState } from "../../types";

interface Props {
  onValueChanged: <T>(key: keyof SubmitQuoteFormState, value: T) => void;
  enableSubmit: boolean;
  onSubmit: VoidFunction;
  error: string | null;
  loading: boolean;
  quoteSubmitted: boolean;
  canSubmitQuote: boolean;
  formState: SubmitQuoteFormState;
  quoteRefetchLoading: boolean;
  onCurrencyChanged: (currency: string) => void;
  carrierTeammatesOpened: boolean;
  closeCarrierTeammates: VoidFunction;
  openCarrierTeammates: VoidFunction;
  shipment: SubmitQuoteFormShipment;
  secondaryAction?: React.ReactNode;
}

export function QuotesOnlyShipmentForm(props: Props) {
  const {
    onValueChanged,
    enableSubmit,
    onSubmit,
    error,
    loading,
    quoteSubmitted,
    canSubmitQuote,
    formState,
    quoteRefetchLoading,
    onCurrencyChanged,
    carrierTeammatesOpened,
    closeCarrierTeammates,
    openCarrierTeammates,
    shipment,
    secondaryAction,
  } = props;
  const onEquipmentTypeChanged = (newValue: string) => {
    onValueChanged<string>("equipmentType", newValue);
  };

  if (!canSubmitQuote) {
    return (
      <Box pt="1rem">
        <Typography variant="body1">
          You already have an active quote on this shipment. To submit a new
          quote, cancel the existing quote.
        </Typography>
      </Box>
    );
  }

  if (quoteRefetchLoading) {
    return (
      <Stack direction="row" justifyContent="center">
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Box pt="1rem">
      <Stack
        spacing={2}
        flexWrap="wrap"
        direction="column"
        justifyContent="flex-start"
      >
        <Alert severity="info">
          The shipper is only looking for quotes/pricing at this time. This is
          not a live load, but it may come available in the future. Learn more
          about Quotes Only shipments{" "}
          <NewTabLink href={QUOTES_ONLY_SHIPMENT_BLOG_URL}>
            <Typography>here</Typography>
          </NewTabLink>
          .
        </Alert>
        {shipment.teammate_quotes?.length > 0 && (
          <Alert severity="info">
            <Stack alignItems="flex-start">
              <Typography>
                Someone from your company has already quoted this load
              </Typography>
              <Button color="primary" onClick={openCarrierTeammates}>
                Click to see who has submitted quotes
              </Button>
            </Stack>
          </Alert>
        )}
        <NumberInput
          inputLabel="Price"
          maxDecimals={2}
          min={0}
          value={formState.price}
          onChange={(val) => onValueChanged<number | null>("price", val)}
          startAdornment={<InputAdornment position="start">$</InputAdornment>}
        />
        <CurrencySelect
          onChange={onCurrencyChanged}
          value={formState.currency}
        />
      </Stack>
      <Box mt=".3rem">
        <Alert severity="info">
          Quotes are{" "}
          {formatQuotingUnitOfMeasure(
            shipment.info.quoting_unit_of_measure,
            false,
          )}{" "}
          (exclusive of tax, unless you explicitly state otherwise).
        </Alert>
      </Box>
      <Box mt="1rem" width="100%">
        <EquipmentTypeSelect
          onChange={onEquipmentTypeChanged}
          value={formState.equipmentType}
        />
      </Box>
      <Box mt="1rem">
        <TextField
          sx={{ width: "100%" }}
          onChange={(e) => onValueChanged<string>("notes", e.target.value)}
          label="Notes (Optional)"
          value={formState.notes}
        />
      </Box>
      <Stack direction="column" spacing={1}>
        {error != null && !loading && (
          <Typography color="red">{error}</Typography>
        )}
        {quoteSubmitted && (
          <Typography color="green">Quote submitted successfully. </Typography>
        )}
      </Stack>
      <Stack sx={{ mt: "1.5rem" }}>
        {!loading ? (
          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              sx={{ p: "1rem" }}
              onClick={onSubmit}
              variant="contained"
              disabled={!enableSubmit}
            >
              Submit Quote
            </Button>
            {secondaryAction}
          </Stack>
        ) : (
          <Stack alignItems="center">
            <CircularProgress />
          </Stack>
        )}
      </Stack>
      <QuotesTeammates
        carrierTeammatesOpened={carrierTeammatesOpened}
        closeCarrierTeammates={closeCarrierTeammates}
        teammates={shipment.teammate_quotes}
      />
    </Box>
  );
}
